// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Service health and runbooks.
 *
 * `OPS-048` requires every page to link to a tested runbook, and `ADM-014`
 * forbids rendering missing telemetry as healthy. A service whose health was
 * not observed therefore shows "Not reported" and says so explicitly, and a
 * missing runbook link is stated rather than silently omitted.
 */

import { capabilities, HealthBadge, queryKeys, type ServiceHealth } from '@eai/ui-shared';
import Link from '@mui/material/Link';
import type { ReactNode } from 'react';

import { ResourceScreen, useControlPlaneQuery } from '../app-shell';

const HTTPS_ONLY = /^https:/i;

/** Service monitoring screen. */
export function MonitoringScreen(): ReactNode {
  const query = useControlPlaneQuery(
    (scope) => queryKeys.controlPlane.monitoredServices(scope),
    (client, signal) => client.listMonitoredServices({ signal }),
  );

  return (
    <ResourceScreen<ServiceHealth>
      title="Monitoring"
      description="Reported service health with owners and runbooks. A service whose health was not observed is shown as unreported, not as healthy."
      requiredCapability={capabilities.monitoringRead}
      area="Monitoring"
      query={query}
      rowKey={(row) => row.service_id}
      emptyTitle="No monitored service is registered"
      emptyDescription="Register a production component in the service catalogue before it can be monitored."
      columns={[
        { key: 'name', header: 'Service', render: (row) => row.name },
        {
          key: 'health',
          header: 'Health',
          render: (row) => (
            <HealthBadge health={row.health} observed={row.observed} subject={row.name} />
          ),
        },
        {
          key: 'release',
          header: 'Release',
          render: (row) => row.release_version ?? 'Not reported',
        },
        { key: 'owner', header: 'Owner', render: (row) => row.owner ?? 'Unassigned' },
        { key: 'checked', header: 'Checked', render: (row) => row.checked_at ?? 'Not reported' },
        {
          key: 'runbook',
          header: 'Runbook',
          render: (row) =>
            row.runbook_url !== null && HTTPS_ONLY.test(row.runbook_url) ? (
              <Link href={row.runbook_url} target="_blank" rel="noopener noreferrer nofollow">
                Open runbook
              </Link>
            ) : (
              'No runbook linked'
            ),
        },
      ]}
    />
  );
}
